import type { MenuOption } from "@dealer-center/core-components";
import {
  buildAccountMenuOptions,
  getAccountName,
  isVinValid,
  mapAccountNamesById,
  useToaster
} from "@dealer-center/core-components";
import { MessagePage } from "@dealer-center/core-custom-components";

import { PageTemplate } from "@dealer-center/core-navbar";
import {
  useAccountFavourites,
  useAccountHistory,
  useGetAccount,
  useGetAccountId,
  useGetAccountNames
} from "@dealer-center/ddc-api-account-fetch";
import type { AccountEntity } from "@dealer-center/ddc-api-account-interface";
import { useNavbarItems } from "@dealer-center/ddc-api-commoncontainer-fetch";
import { useGetUser } from "@dealer-center/ddc-api-user-fetch";
import { useNavigateToAccount } from "@dealer-center/ddc-core-utils";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

export function CoreNavbar({ children }: { children: React.ReactNode }) {
  const {
    data: userData,
    isLoading: isUserLoading,
    error: userError
  } = useGetUser();
  const {
    error: navbarError,
    isLoading: isNavbarLoading,
    data: navbarData
  } = useNavbarItems();
  const [accountToSearchFor, setAccountToSearchFor] = useState<
    string | undefined
  >();
  const { toastError } = useToaster();
  const { data: foundAccount } = useGetAccount({
    accountId: accountToSearchFor
  });

  const currentAccount = useGetAccountId();
  const navigate = useNavigate();
  const accountNavigator = useNavigateToAccount();
  useEffect(() => {
    if (foundAccount && foundAccount.id === accountToSearchFor) {
      accountNavigator.navigateToAccount(foundAccount.id);
    }
  }, [accountNavigator, accountToSearchFor, foundAccount]);
  useEffect(() => {
    if (userError) {
      toastError("Error loading user data" + userError);
    }
  }, [userError]);
  useEffect(() => {
    if (navbarError) {
      toastError("Error loading navbar items" + navbarError);
    }
  }, [navbarError]);

  const { availableAccounts, selectedAccountName } = useGetAvailableAccounts();

  const accountStatus = getAccountStatusForNavbar(foundAccount);
  if (isUserLoading)
    return (
      <MessagePage
        actionName={"Loading user state"}
        message="Loading user information"
      />
    );
  if (isNavbarLoading)
    return (
      <MessagePage
        actionName={"Loading navbar items"}
        message="Loading navbar items"
      />
    );

  return (
    <PageTemplate
      availableAccounts={availableAccounts}
      hasBridgeBar={true}
      linksMenu={navbarData?.linksMenu}
      nav={navbarData?.usermenu.nav}
      selectAccount={account => {
        accountNavigator.navigateToAccount(account);
      }}
      selectedAccount={currentAccount}
      selectedAccountName={selectedAccountName}
      selectedAccountStatus={accountStatus}
      user={{
        name: userData?.first + " " + userData?.last
      }}
      onSearch={value => {
        if (isVinValid(value.inputValue)) {
          navigate("/vin-decode/US/" + value.inputValue + "/");
          return;
        }
        setAccountToSearchFor(value.inputValue);
      }}
    >
      {children}
    </PageTemplate>
  );

  function getAccountStatusForNavbar(
    foundAccount: AccountEntity | undefined
  ): string | undefined {
    if (!foundAccount) return undefined;
    const status = foundAccount.status;
    if (status.toLowerCase() === "active") return undefined;
    return status;
  }

  function useGetAvailableAccounts(): {
    availableAccounts: MenuOption[];
    selectedAccountName?: string;
  } {
    const { accountFavorites } = useAccountFavourites();
    const { accountHistory } = useAccountHistory();
    const { data: accountNames } = useGetAccountNames([
      ...(accountFavorites ?? []),
      ...(accountHistory ?? [])
    ]);
    // The current account's name is fetched on its own, for the top-bar label
    // only. Keeping it out of the menu's request keeps that request's cache key
    // unchanged when the user switches accounts, so the menu labels and the
    // favorites order don't drop back to ids while a new request runs.
    const { data: currentAccountNames } = useGetAccountNames(
      currentAccount ? [currentAccount] : []
    );
    const availableAccounts = useMemo(
      () =>
        buildAccountMenuOptions(
          accountFavorites ?? [],
          accountHistory ?? [],
          accountNames
        ),
      [accountFavorites, accountHistory, accountNames]
    );
    const selectedAccountName = currentAccount
      ? getAccountName(mapAccountNamesById(currentAccountNames), currentAccount)
      : undefined;
    return { availableAccounts, selectedAccountName };
  }
}

export default CoreNavbar;
